import { useState, useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { COLORS, FONTS, staggerContainer, staggerItem } from "./Hero.styles";

const STATS = [
  { end: 40, suffix: "+", decimals: 0, label: "Enterprise Deployments" },
  { end: 20, suffix: "+", decimals: 0, label: "Languages Supported" },
  { end: 99.9, suffix: "%", decimals: 1, label: "System Availability" },
];

/**
 * Count-up that only runs once, triggered when the number scrolls into view.
 * Respects prefers-reduced-motion by jumping straight to the end value.
 */
function CountUp({ end, suffix, decimals }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setValue(end);
      return;
    }

    let cur = 0;
    const duration = 1400;
    const stepMs = 16;
    const steps = duration / stepMs;
    const increment = end / steps;
    const timer = setInterval(() => {
      cur += increment;
      if (cur >= end) {
        setValue(end);
        clearInterval(timer);
      } else {
        setValue(cur);
      }
    }, stepMs);
    return () => clearInterval(timer);
  }, [inView, end]);

  const display = decimals > 0 ? value.toFixed(decimals) : Math.floor(value);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
}

/**
 * HeroStats
 *
 * Glassmorphism cards, staggered entrance, subtle hover lift + soft shadow.
 * No continuous animation loops — entrance plays once, hover is the only
 * interactive motion, and it's a single transform + shadow transition.
 */
export default function HeroStats() {
  return (
    <Box
      component={motion.div}
      {...staggerContainer(0.1, 0.3)}
      sx={{ mt: { xs: "36px", md: "44px" } }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing="14px"
        sx={{
          justifyContent: { xs: "center", lg: "flex-start" },
        }}
      >
        {STATS.map((stat) => (
          <Box
            key={stat.label}
            component={motion.div}
            variants={staggerItem}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            sx={{
              px: "20px",
              py: "18px",
              borderRadius: "14px",
              background: COLORS.glassBg,
              border: `1px solid ${COLORS.border}`,
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              minWidth: { sm: "150px" },
              flex: { sm: "1 1 0" },
              maxWidth: { sm: "200px" },
              textAlign: { xs: "center", lg: "left" },
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                borderColor: COLORS.borderHover,
                boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: FONTS.display,
                fontWeight: 800,
                fontSize: "clamp(1.6rem, 2.4vw, 2.1rem)",
                color: COLORS.white,
                letterSpacing: "-0.5px",
                lineHeight: 1,
                mb: "6px",
              }}
            >
              <CountUp end={stat.end} suffix={stat.suffix} decimals={stat.decimals} />
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTS.body,
                fontSize: "0.74rem",
                color: COLORS.textFaint,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}